import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { DateTime } from "luxon";
import { appContext } from "../AppContext";
import Helper from "../Common/Helper";
import { RouteHelper } from '../Common/RouteHelper'
import { ApiError, IAxiosRequestConfig } from "@iconics/core";
import ApiMessageBuilder from "./ApiMessageBuilder";
import Authentication, { LogOutReason } from "../Common/Authentication";
import { createBrowserHistory } from 'history';

export class ApiClient implements IApiClient
{
    private readonly authentication: Authentication;
    private readonly routeHelper = new RouteHelper();
    private get alert() { return appContext().alert; }
    private messageBuilder = new ApiMessageBuilder();
    private get local() { return appContext().localStorageProvider; }
    private get session() { return appContext().sessionStorageProvider; }

    constructor(authentication: Authentication)
    {
        this.authentication = authentication;
    }

    public initialise(): void
    {
        const productName = "IBSS Hub";
        const version = process.env.REACT_APP_VERSION;

        axios.interceptors.request.use(request =>
        {
            request.headers["App-Version"] = `${productName}/${version}`;

            const authToken = this.authentication.getToken();
            if (authToken)
            {
                request.headers["Authorization"] = `bearer ${authToken}`;
            }

            const currentLang = appContext().language.current;
            if (currentLang != null && currentLang !== "")
            {
                request.headers["Accept-Language"] = currentLang;
            }

            return request;
        });

        axios.interceptors.response.use(
            response =>
            {
                return response;
            },
            (error: AxiosError) =>
            {
                const config = error.config as IAxiosRequestConfig | undefined;
                const statusCode = error?.response?.status;
                const route = this.routeHelper.getCurrentRoute();
                const isPublic = (route?.isPublic ?? false);
                const isAuthenticationRoute = route?.path == "/auth";
                const suppressErrorPopup = (config?.suppressErrorPopup ?? false);

                if (statusCode === 401 && !isPublic)
                {
                    // log out if we get another 401 after refreshing the access token (to guard against an infinite loop)
                    if (!config || config.tokenRefreshed == true)
                    {
                        this.authentication.logOut(LogOutReason.SessionExpired);
                        return Promise.reject(error);
                    }

                    return this.authentication
                        .refreshToken()
                        .then(token =>
                        {
                            config.tokenRefreshed = true;
                            config.headers["Authorization"] = `bearer ${token}`;
                            return axios(config);
                        })
                        .catch(() =>
                        {
                            this.authentication.logOut(LogOutReason.SessionExpired);
                            return Promise.reject(error);
                        });
                }
                else if (statusCode === 403 && !isPublic)
                {
                    if (!suppressErrorPopup)
                    {
                        let titleAndMessage = this.messageBuilder.getTitleAndMessage(error);
                        this.alert.show(titleAndMessage.title, titleAndMessage.message, undefined, { messageIsHtml: titleAndMessage.isMessageHtml });
                    }
                    return Promise.reject(error);
                }
                else if (statusCode === 403 && isAuthenticationRoute)
                {
                    this.authentication.logOut(LogOutReason.AccountIncomplete);
                    return Promise.reject(error);
                }
                else
                {
                    if (!suppressErrorPopup)
                    {
                        let titleAndMessage = this.messageBuilder.getTitleAndMessage(error);
                        this.alert.show(titleAndMessage.title, titleAndMessage.message, undefined, { messageIsHtml: titleAndMessage.isMessageHtml });
                    }
                    return Promise.reject(error);
                }
            });
    }
}

export interface IApiClient
{
    initialise(): void;
}
